import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Divider,
  Tooltip,
  Grid,
  Fade,
} from '@mui/material';
import FacebookIcon from '@mui/icons-material/Facebook';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import GitHubIcon from '@mui/icons-material/GitHub';
import PolicyIcon from '@mui/icons-material/Policy';
// import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee'; // commented - Pricing quick link hidden
// import AttachMoneyIcon from '@mui/icons-material/AttachMoney'; // commented - Pricing quick link hidden
import FavoriteIcon from '@mui/icons-material/Favorite';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useNavigate } from 'react-router-dom';
import AOS from 'aos';
import 'aos/dist/aos.css';
// import { detectCurrency, detectCurrencySync } from '../utils/currencyDetection'; // commented - only used for Pricing quick link

const socialLinks = [
  {
    icon: <LinkedInIcon />,
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/bixbite-ai/',
    gradient: 'linear-gradient(135deg, #0077b5 0%, #00a0dc 100%)',
  },
  {
    icon: <TwitterIcon />,
    label: 'Twitter',
    href: 'https://twitter.com/bixbiteai',
    gradient: 'linear-gradient(135deg, #1da1f2 0%, #4fc3f7 100%)',
  },
  {
    icon: <FacebookIcon />,
    label: 'Facebook',
    href: 'https://facebook.com/bixbiteai',
    gradient: 'linear-gradient(135deg, #1877f2 0%, #42a5f5 100%)',
  },
  {
    icon: <GitHubIcon />,
    label: 'GitHub',
    href: 'https://github.com/bixbiteai',
    gradient: 'linear-gradient(135deg, #333 0%, #666 100%)',
  },
];

const Footer = () => {
  const navigate = useNavigate();
  // const [useINR, setUseINR] = useState(detectCurrencySync()); // commented - only used for Pricing quick link

  // Detect currency based on IP location - commented (only used for Pricing quick link)
  // useEffect(() => {
  //   detectCurrency().then((isINR) => setUseINR(isINR));
  // }, []);

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  const quickLinks = [
    { label: 'Privacy Policy', icon: <PolicyIcon />, path: '/privacy-policy' },
    // { label: 'Pricing', icon: useINR ? <CurrencyRupeeIcon /> : <AttachMoneyIcon />, path: '/bixbite_pricing' },
  ];

  return (
    <Box
      component="footer"
      sx={{
        position: 'relative',
        bgcolor: '#004d40',
        color: 'white',
        pt: { xs: 6, md: 8 },
        pb: { xs: 4, md: 5 },
        px: { xs: 3, md: 6 },
        overflow: 'hidden',
      }}
    >
      {/* Background Decorations */}
      <Box
        sx={{
          position: 'absolute',
          top: -100,
          right: -100,
          width: 300,
          height: 300,
          borderRadius: '50%',
          background: 'rgba(0, 121, 107, 0.1)',
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: -50,
          left: -50,
          width: 200,
          height: 200,
          borderRadius: '50%',
          background: 'rgba(77, 182, 172, 0.1)',
          filter: 'blur(60px)',
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', position: 'relative', zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Brand Section */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={600}>
              <Box data-aos="fade-right">
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AutoAwesomeIcon sx={{ fontSize: 28, color: '#4db6ac', mr: 1 }} />
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      background: 'linear-gradient(135deg, #4db6ac 0%, #80cbc4 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    Bixbite AI
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    lineHeight: 1.7,
                    maxWidth: 280,
                  }}
                >
                  This website is also created by Bixbite.ai — Building the future of software development with AI.
                </Typography>
              </Box>
            </Fade>
          </Grid>

          {/* Quick Links */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={800}>
              <Box sx={{ textAlign: { xs: 'left', md: 'center' } }} data-aos="fade-up">
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: '#4db6ac',
                  }}
                >
                  Quick Links
                </Typography>
                <Stack 
                  direction="row" 
                  spacing={2} 
                  justifyContent={{ xs: 'flex-start', md: 'center' }}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {quickLinks.map((link) => (
                    <Box
                      key={link.label}
                      onClick={() => navigate(link.path)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        px: 2,
                        py: 1,
                        borderRadius: '10px',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.1)',
                          transform: 'translateY(-2px)',
                        },
                      }}
                    >
                      <Box sx={{ color: '#4db6ac', display: 'flex' }}>
                        {React.cloneElement(link.icon, { sx: { fontSize: 18 } })}
                      </Box>
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {link.label}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>

          {/* Social Links */}
          <Grid item xs={12} md={4}>
            <Fade in timeout={1000}>
              <Box sx={{ textAlign: { xs: 'left', md: 'right' } }} data-aos="fade-left">
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    mb: 2,
                    color: '#4db6ac',
                  }}
                >
                  Connect With Us
                </Typography>
                <Stack 
                  direction="row" 
                  spacing={1.5} 
                  justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                >
                  {socialLinks.map((social, idx) => (
                    <Tooltip key={social.label} title={social.label} arrow>
                      <IconButton
                        aria-label={social.label}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          width: 44,
                          height: 44,
                          background: 'rgba(255, 255, 255, 0.08)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          color: 'white',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: social.gradient,
                            transform: 'translateY(-4px) scale(1.1)',
                            boxShadow: '0 8px 20px -8px rgba(0, 0, 0, 0.4)',
                          },
                          '& svg': { fontSize: 20 },
                        }}
                      >
                        {social.icon}
                      </IconButton>
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>
        </Grid>

        <Divider 
          sx={{ 
            my: 4, 
            borderColor: 'rgba(255, 255, 255, 0.1)',
          }} 
        />

        {/* Copyright */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            &copy; {new Date().getFullYear()} Bixbite AI. All rights reserved. Made with
            <FavoriteIcon sx={{ fontSize: 14, color: '#ef5350', mx: 0.5 }} />
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Footer;
